import {
    productDetail_productDetail,
    productDetail_productDetail_customisation_estimates,
    productDetail_productDetail_locations,
    productDetail_productDetail_categories,
    productDetail_productDetail_margin_tier_prices,
    productDetail_productDetail_media_gallery,
} from 'src/queries/__generated__/productDetail';
import {
    DEFAULT_PRICE,
    MediaEntry,
    ProductType,
    StockStatus,
    MediaGalleryEntry,
    ProductItemBySku,
} from 'src/types/global-types';
import { Swatch } from 'src/components/ProductDetail/ProductSwatch';
import { ProductConfigOptions } from 'src/components/ProductDetail/ProductOptions';
import { Size } from 'src/components/ProductDetail/ProductSizes';
import { TierPrice } from 'src/components/ProductDetail/TieredPricing';
import { imagePath } from 'src/util/formatUrl';
import { ApplicationMethod, ProductLocation } from 'src/components/CustomisationPage/feature/customisation.types';
import { DEFAULT_COLOR_SWATCH } from 'src/components/ProductDetail/utils/swatchData';
import { cleanLocations } from 'src/components/CustomisationPage/utils/cleanLocations';
import { productList } from 'src/queries/__generated__/productList';
import { productVariantFromBase } from 'src/components/ProductDetail/utils/product-options';
import { bundleProductDetail_bundleProductDetail } from 'src/queries/__generated__/bundleProductDetail';
import { wweBundle_bundleProductDetail } from 'src/queries/__generated__/wweBundle';
import { WearerSummary } from 'src/components/BundleProduct/BundleOptionsProvider';
import { wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items } from 'src/queries/__generated__/wearerBundleDetail';
import { upsell_upsell_items_ConfigurableProduct } from 'src/queries/__generated__/upsell';
import { productPrice_productPrice } from 'src/queries/__generated__/productPrice';
import { sortBy } from 'es-toolkit/compat';
import Bugsnag from '@bugsnag/js';

export type PriceEstimate = {
    min_qty: number;
    price: number;
    price_incl: number;
    type: ApplicationMethod;
};

export type ColourPickerSwatch = {
    _typename: 'ColorSwatch';
    color_id: string;
    kind: string;
    label: string;
    product_small_url: string;
    value: string;
    swatch_image_url?: string | null;
};

export type Category = {
    name: string;
    url_path: string | null;
    id?: number | null;
    breadcrumb_label?: string;
};

export type PreviousPrice = {
    price_exl: string;
    price_inc: string;
};

export type CamelizedCategory = {
    name: string;
    urlPath: string;
};

export type UpsellCategory = {
    name: string;
    breadcrumb_label: string;
    url_path: string;
};

export interface BaseProduct {
    name: string;
    id: number;
    sku: string;
    promo_banner: string | null;
    url_key?: string;
    type: ProductType;
    media_entries: MediaEntry[];
    media_gallery_entries: MediaGalleryEntry[];
    small_image: string;
    description: string | null;
    short_description: string | null;
    locations: ProductLocation[];
    size_guides: string;
    is_24_hour_delivery: boolean;
    can_customise: boolean;
    brand_badge: string;
    shippingType: string;
    meta_description: string | null;
    meta_keywords: string | null;
    meta_title: string | null;
    categories: Category[];
    price_estimates: PriceEstimate[];
    recommended_application?: ApplicationMethod;
    default_colour_option?: number;
    default_size?: number;
    product_tags?: string;
    price_range?: PriceRange;
    previous_prices?: PreviousPrice;
    upsell_products?: ProductItemBySku[];
    crosssell_products?: ProductItemBySku[];
    upsell_categories?: UpsellCategory[];
    brand?: number;
    gender?: number;
    default_category?: string;
    default_category_url?: string;
    breadcrumb_label?: string;
    material?: string;
    filter_body_fit?: string;
    more_from_brand?: ProductItemBySku[];
    meta_description_category?: string;
    cutoff_time?: string[];
}

export type PriceRange = {
    maximum_price: PriceRangeValues;
    minimum_price: PriceRangeValues;
};

type PriceRangeValues = {
    final_price_excl_formatted: string;
    final_price_formatted: string;
    regular_price_excl_formatted: string;
    regular_price_formatted: string;
};

export interface Product extends BaseProduct {
    margin_tier_prices: TierPrice[];
    price_estimates: PriceEstimate[];
}

export type BundleProduct = BaseProduct;

export const EMPTY_PRODUCT: Product = {
    name: '',
    sku: '',
    id: -1,
    url_key: undefined,
    promo_banner: '',
    type: ProductType.SimpleProduct,
    media_entries: [],
    media_gallery_entries: [],
    margin_tier_prices: [],
    small_image: 'https://placehold.it/200/ffffff',
    description: '',
    short_description: '',
    locations: [],
    price_estimates: [],
    size_guides: '...',
    is_24_hour_delivery: false,
    can_customise: false,
    brand_badge: '',
    shippingType: '',
    meta_description: '',
    meta_keywords: '',
    meta_title: '',
    categories: [],
    product_tags: '',
    meta_description_category: '',
};

const { price_estimates, margin_tier_prices, ...rest } = EMPTY_PRODUCT;
const EMPTY_BUNDLE_PRODUCT: BundleProduct = {
    price_estimates,
    ...rest,
};

const getBrandBadge = (cat: (productDetail_productDetail_categories | null)[] | null) => {
    if (cat?.length === 0) {
        return '';
    }

    const brands = cat?.filter((el) => el?.url_path?.includes('brands/'));
    if (brands?.length === 0) {
        return '';
    }

    const getOneBrand = brands?.filter((el) => {
        const firstIndex = el?.url_path?.indexOf('/');
        const lastIndex = el?.url_path?.lastIndexOf('/');
        if (firstIndex === lastIndex) {
            return el?.url_path;
        }
    });

    return (getOneBrand && getOneBrand[0]?.url_path?.slice(7)) || '';
};

export function cleanBaseProduct(first) {
    if (!first) {
        return EMPTY_PRODUCT;
    }

    const locations = cleanLocations(first.locations as productDetail_productDetail_locations[]);
    const brand = getBrandBadge(first.categories);

    return {
        brand: first.brand,
        gender: first.gender,
        name: first.name || 'unknown',
        sku: first.sku || 'unknown',
        id: first.id!,
        promo_banner: first.promo_banner,
        type: ProductType[first.__typename],
        description: first.description?.html || 'There is no information for this product.',
        short_description: first.short_description?.html || '',
        media_entries: cleanMediaEntries((first.media_gallery as productDetail_productDetail_media_gallery[]) || []),
        media_gallery_entries: first.media_gallery_entries,
        small_image: imagePath(first.small_image?.url),
        locations: locations,
        size_guides: first.size_guides?.content || '',
        is_24_hour_delivery: first.is_24_hour_delivery,
        can_customise: first.can_customise,
        url_key: first.url_key,
        brand_badge: brand,
        shippingType: first.shipperhq_shipping_group,
        meta_description: first.meta_description,
        meta_keywords: first.meta_keyword,
        meta_title: first.meta_title,
        categories: first.categories as productDetail_productDetail_categories[],
        price_estimates: [],
        product_tags: first.product_tags,
        price_range: first.price_range,
        previous_prices: first.previous_prices,
        crosssell_products: first.crosssell_products,
        upsell_products: first.upsell_products,
        material: first.material,
        filter_body_fit: first.filter_body_fit,
        upsell_categories: first.upsell_categories,
        more_from_brand: first.more_from_brand,
        meta_description_category: first.meta_description_category,
        cutoff_time: first.cutoff_time,
    };
}

export function cleanProduct(first: productDetail_productDetail | undefined): Product {
    if (!first) {
        return EMPTY_PRODUCT;
    }

    const base = cleanBaseProduct(first);

    return {
        ...base,
        margin_tier_prices: productTierPrices(first),
        price_estimates: first.customisation_estimates as productDetail_productDetail_customisation_estimates[],
        categories: first.categories as productDetail_productDetail_categories[],
        default_colour_option: first.default_colour_option,
        default_size: first.default_size,
        default_category: first?.default_category,
        default_category_url: first?.default_category_url,
        breadcrumb_label: first?.breadcrumb_label,
    };
}

export function cleanBundleProduct(first: bundleProductDetail_bundleProductDetail | undefined): BundleProduct {
    if (!first) {
        return EMPTY_BUNDLE_PRODUCT;
    }

    const base = cleanBaseProduct(first);
    return base;
}

export function mixAndMatchCleanWearerSummary(items): WearerSummary[] {
    const summaries: WearerSummary[] = [];

    items.forEach((bundleItem) =>
        bundleItem.target_configurable.forEach((item) => {
            if (!item) {
                Bugsnag.notify('Item was null');
                return;
            }
            summaries.push({
                id: item.id || -1,
                name: item.name || 'unknown',
                sku: item.sku || 'unknown-sku',
                image: imagePath(item.small_image?.url),
                maxQty: bundleItem.max_qty ?? 1,
                position: bundleItem.position ?? 1,
            });
        }),
    );

    return summaries;
}

export function cleanWearerSummary(items: GqlBundleItem[]): WearerSummary[] {
    const summaries: WearerSummary[] = [];
    items.forEach((item) => {
        if (!item) {
            Bugsnag.notify('Item was null');
            return;
        }
        if (!item.target_configurable) {
            return;
        }
        const targetConfigurable = item.target_configurable?.find((element) => element?.option_id === item.option_id);

        if (!targetConfigurable) {
            return;
        }
        summaries.push({
            id: targetConfigurable.id || -1,
            name: targetConfigurable.name || 'unknown',
            sku: targetConfigurable.sku || 'unknown-sku',
            image: imagePath(targetConfigurable.small_image?.url),
            maxQty: item.max_qty ?? 1,
            position: item.position ?? 1,
        });
    });
    return summaries;
}

type CleanedMultiProductLocations = ProductLocation[][];
type GqlBundleItem = wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct_bundle_items | null;

type ProductLocationCount = ProductLocation & { count: number };

export function concatLocationArray(items: CleanedMultiProductLocations): ProductLocationCount[] {
    const locations: ProductLocationCount[] = [];
    const max = items.length;
    items.forEach((item) => {
        item.forEach((location) => {
            //If the location has already been added in the array update:
            if (locations.some((el) => el.name === location.name)) {
                //Get the index of the matched item
                const matched = locations.findIndex((value) => value.name === location.name);
                //Get the current count number
                const currentCount = locations[matched].count;
                //Update the value in the array
                locations[matched].count = currentCount + 1;
            } else {
                locations.push({
                    name: location.name,
                    image_url: location.image_url,
                    method: location.method,
                    count: 1,
                });
            }
        });
    });
    return locations.filter((location) => location.count === max);
}

export function cleanBundlePrice(items: (wweBundle_bundleProductDetail | productPrice_productPrice | null)[] | null) {
    if (!items || !items[0]) {
        return {
            regular_price_incl: 0,
            regular_price: 0,
            final_price_incl: 0,
            final_price: 0,
            price_incl: 0,
            price: 0,
        };
    }

    const first = items[0];

    return {
        final_price_incl: parseFloat(first.price_range?.minimum_price?.final_price_formatted?.replace(/,/g, '') || '0'),
        final_price: parseFloat(first.price_range?.minimum_price?.final_price_excl_formatted?.replace(/,/g, '') || '0'),
        regular_price_incl: parseFloat(
            first.price_range?.minimum_price?.regular_price_formatted.replace(/,/g, '') || '0',
        ),
        regular_price: parseFloat(
            first.price_range?.minimum_price?.regular_price_excl_formatted.replace(/,/g, '') || '0',
        ),
        price_incl: parseFloat(first.price_range?.minimum_price?.regular_price_formatted.replace(/,/g, '') || '0'),
        price: parseFloat(first.price_range?.minimum_price?.regular_price_excl_formatted.replace(/,/g, '') || '0'),
    };
}

const DEFAULT_IMAGE: MediaEntry = {
    large_height: 1200,
    large_width: 1200,
    large_url: 'https://placehold.it/1200/ffffff',
    medium_url: 'https://placehold.it/500/ffffff',
    small_url: 'https://placehold.it/200/ffffff',
    media_type: 'image',
    position: 0,
    label: 'image',
};

export function cleanMediaEntries(media: productDetail_productDetail_media_gallery[]): MediaEntry[] {
    if (!media || !Array.isArray(media)) return [DEFAULT_IMAGE];
    const images = media.map((item) => {
        const large = item.url;
        const medium = item.medium_url;
        const small = item.small_url;
        return {
            large_height: item.large_height || 1200,
            large_width: item.large_width || 1200,
            large_url: imagePath(large),
            medium_url: imagePath(medium),
            small_url: imagePath(small),
            media_type: 'image',
            position: item.position,
            label: item.label || 'unknown',
        };
    });

    return sortBy(images, ['position']);
}

export type ConfigurableAttributeOption = {
    code: string;
    label: string;
    value_index: number;
};

export type CamelizedConfigurableAttributeOption = {
    code: string;
    label: string;
    valueIndex: number;
};

export interface BaseVariant {
    sku: string;
    id: number;
    attributes: ConfigurableAttributeOption[];
    size_id: number;
    color_id: number;
    stock_status: StockStatus;
    max_qty: number | null;
    image_url?: string | null;
    colour_family?: string;
    colour_family_label?: string;
    hide_stock_source?: boolean;
}

export interface CamelizedBaseVariant {
    sku: string;
    id: number;
    attributes: CamelizedConfigurableAttributeOption[];
    sizeIdd: number;
    colorId: number;
    stockStatus: StockStatus;
    maxQty: number | null;
    imageUrl?: string | null;
    colourFamily?: string;
    colourFamilyLabel?: string;
}

export interface ProductVariant extends BaseVariant {
    media_entries: MediaEntry[];
    small_image: string;
}

const EMPTY_ARRAY = [];

export function optionsToColorSwatches(options?: ProductConfigOptions[]): Swatch[] {
    const attr = (options || EMPTY_ARRAY).find((x) => x.attribute_code === 'color');
    if (!attr) return [];
    return attr.values.map((x) => {
        return {
            title: x.label,
            hex: 'grey',
            value_index: x.value_index,
            swatch_data: x.swatch_data || DEFAULT_COLOR_SWATCH,
        };
    });
}

export function optionsToSizes(options?: ProductConfigOptions[]): Size[] {
    const attr = (options || EMPTY_ARRAY).find((x) => x.attribute_code === 'size');
    if (!attr) return [];
    return attr.values.map((x) => {
        return {
            id: x.value_index,
            stock: StockStatus.InStock,
            title: x.label,
            /**
             * NOTE: This is a placeholder - once enough options are selected
             * to enable a variant to be chosen (eg: resolved down to a simple)
             * then this id should be changed
             */
            simpleProductId: -1,
            simpleProductSku: null,
            maxQty: null,
        };
    });
}

export function productTierPrices(
    product: productDetail_productDetail | upsell_upsell_items_ConfigurableProduct,
): TierPrice[] {
    if (!product.margin_tier_prices) return EMPTY_ARRAY;
    const qty1 = product.margin_tier_prices.find((x) => x && x.quantity === 1);
    const prices: TierPrice[] = (product.margin_tier_prices as productDetail_productDetail_margin_tier_prices[]).map(
        (x) => {
            return {
                qty: x.quantity ?? 0,
                discount: x.discount_percentage ?? 0,
                price: x.price ?? 0,
                price_incl: x.price_incl ?? 0,
            };
        },
    );

    if (!qty1) {
        const qty1Price: TierPrice = { price_incl: 0, price: 0, discount: 0, qty: 1 };
        Bugsnag.notify('Margin Tier price with a qty of 1 missing for sku', (event) => {
            event.addMetadata('productData', { sku: product.sku });
        });
        return [qty1Price].concat(prices);
    }

    return prices;
}

export function hydrateBaseVariants(baseVariants: BaseVariant[], data?: productList | null): ProductVariant[] {
    if (!data || !Array.isArray(data.productList)) return [];
    const outgoing: ProductVariant[] = [];
    const map = new Map(data.productList.map((item) => [item?.id, item]));
    baseVariants.forEach((variant) => {
        const match = map.get(variant.id);
        if (match) {
            const next = productVariantFromBase(variant, match);
            outgoing.push(next);
        }
    });
    return outgoing;
}

export function qtyOnePrice(tierPrices: TierPrice[]) {
    const qty1 = tierPrices.find((x) => x && x.qty === 1);
    if (qty1) {
        return {
            regular_price_incl: 0,
            regular_price: 0,
            final_price_incl: 0,
            final_price: 0,
            price: qty1.price,
            price_incl: qty1.price_incl,
        };
    }
    return DEFAULT_PRICE;
}

export interface QuoteDataShippingMethodInterface {
    carrier_code: string;
    method_code: string;
    carrier_title?: string;
    method_title?: string;
    amount: number;
    base_amount: number;
    available: boolean;
    extension_attributes?: QuoteDataShippingMethodExtensionInterface;
    error_message: string;
    price_excl_tax: number;
    price_incl_tax: number;
}
export interface QuoteDataShippingMethodExtensionInterface {
    tooltip?: string;
    custom_duties?: string;
    hide_notifications?: string;
    delivery_date?: string;
    display_range?: number;
    cutoff_time?: string[];
}

export enum ProductTagsLabels {
    CallUs = 'Call Us Tier',
    Print = 'Print Available',
    Embroidery = 'Embroidery Available',
}

export enum MetadataAttributeCodes {
    DefaultColorOption = 'default_colour_option',
    DefaultSize = 'default_size',
    ProductTags = 'product_tags',
}
